import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Image, Platform, ScrollView, View } from 'react-native';

import styles from './comprehension-choose.style';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useScroll } from '../../../hooks/use-scroll';
import { arrayShuffle } from '../../../utils/array';
import trueBeginnerStr from '../../../utils/true-beginner-str';
import { ContinueButton } from '../../continue-button/continue-button';
import { Instruction } from '../../instruction/instruction.component';
import { OptionChooseContainer } from '../../option-choose-container/option-choose-container';
import { ThemedView } from '../../themed-view';
import { Title } from '../../title/title.component';
import { TranslatableText } from '../../translatable-text/translatable-text';
import { Card } from '../card/card';

export function ComprehensionChoose() {
  const {
    currentStep,
    currentActivity,
    isCurrentStepComplete,
    goNext,
    completeCurrentStep,
  } = useActivityContext();

  const scrollRef = useRef<ScrollView>(null);
  const [correctFeedback, setCorrectFeedback] = useState(true);
  const [questionOpacity] = useState(new Animated.Value(1));
  const [showNextButton, setShowNextButton] = useState(false);
  const { scrollToEnd } = useScroll(scrollRef);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    setOptions(
      arrayShuffle([currentStep?.answer, ...(currentStep?.options ?? [])])
    );
    disableOptions(false);
  }, [currentStep]);

  const [optionsAreDisabled, disableOptions] = useState(false);
  const [index, setIndex] = useState(0);

  const onAnswer = (isCorrect: boolean) => {
    disableOptions(true);
    setTimeout(() => {
      setCorrectFeedback(isCorrect);
      completeCurrentStep(isCorrect);
      setShowNextButton(true);
    }, 500);
  };

  useEffect(() => {
    if (isCurrentStepComplete) {
      scrollToEnd();
    }
  }, [isCurrentStepComplete, scrollToEnd]);

  const resetAndGoNextStep = () => {
    setShowNextButton(false);
    setTimeout(() => {
      goNext();
      setIndex((x) => x + 1);
      questionFadeIn();
    }, 800);
    questionFadeOut();
    disableOptions(false);
    setIndex((x) => x + 1);
  };

  const questionFadeOut = useCallback(() => {
    Animated.timing(questionOpacity, {
      toValue: 0,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, [questionOpacity]);

  const questionFadeIn = useCallback(() => {
    Animated.timing(questionOpacity, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, [questionOpacity]);

  return (
    <>
      <ScrollView
        ref={scrollRef}
        style={styles.section}
        contentContainerStyle={styles.container}
      >
        <View style={styles.content}>
          <Instruction
            text={trueBeginnerStr(
              currentActivity,
              'instructions',
              currentActivity.trueBeginner
            )}
          />
          {!!currentStep?.image && (
            <Image style={styles.image} source={{ uri: currentStep?.image }} />
          )}
          <ThemedView style={styles.viewBox} />
          <View style={[styles.view]}>
            <Card containerStyle={styles.card}>
              <TranslatableText
                style={styles.card}
                translation={currentActivity.textTranslation}
                isMarkdown
                isHtml
              >
                {currentActivity.text}
              </TranslatableText>
            </Card>
          </View>
          <ThemedView style={[styles.viewText]}>
            <Animated.View style={[{ opacity: questionOpacity }]}>
              <Title>{currentStep?.text}</Title>
            </Animated.View>
          </ThemedView>
        </View>
        <OptionChooseContainer
          containerStyle={styles.optionContainer}
          disabled={optionsAreDisabled}
          answer={currentStep?.answer}
          options={options}
          onAnswer={onAnswer}
          index={index}
          scrollToEnd={scrollToEnd}
          checkButtonActive={Platform.OS !== 'web'}
        />
      </ScrollView>
      {showNextButton && (
        <ContinueButton
          feedbackTitle={currentStep?.feedbackTitle}
          feedback={currentStep?.feedback}
          isCorrect={correctFeedback}
          onPress={resetAndGoNextStep}
        />
      )}
    </>
  );
}
